import {
  Container,
  Stack,
  Typography,
  Card,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TablePagination,
  Box,
  Grid,
  Chip,
  Avatar,
  Divider,
  Fade,
  Zoom,
  Tooltip,
  Paper,
  Button,
  CardContent,
  TextField,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { UserListHead, UserListToolbar } from '../../../layouts/user';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MiscSkeleton from '../../../layouts/user/MiscSkeleton';
import { GetAll, apiURL } from '../../../utils/apiCalls';
import { filter } from 'lodash';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import GavelIcon from '@mui/icons-material/Gavel';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { capitalCase } from 'change-case';
import SearchNotFound from '../../../layouts/SearchNotFound';

// ----------------------------------------------------------------------

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`procurement-tabpanel-${index}`}
      aria-labelledby={`procurement-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TABLE_HEAD = [
  { id: 'no', label: 'SNo.', align: 'left' },
  { id: 'name', label: 'Name', align: 'left' },
  { id: 'email', label: 'Email', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'subscription', label: 'Subscription', align: 'left' },
  { id: 'created_at', label: 'Registered', align: 'left' },
  { id: 'actions', label: 'Actions', align: 'center' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  // Ensure array is actually an array
  if (!Array.isArray(array)) {
    console.warn('applySortFilter received non-array:', array);
    return [];
  }
  
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  
  let filteredArray = stabilizedThis.map((el) => el[0]);
  
  // Sort by status with verified/approved on top
  filteredArray.sort((a, b) => {
    const statusOrder = { 'approved': 1, 'verified': 1, 'pending': 2, 'rejected': 3 };
    const aOrder = statusOrder[a.status?.toLowerCase()] || 2;
    const bOrder = statusOrder[b.status?.toLowerCase()] || 2;
    return aOrder - bOrder;
  });
  
  if (query) {
    return filter(filteredArray, (_user) =>
      JSON.stringify(_user['first_name'] + ' ' + _user['last_name']).toLowerCase().trim().indexOf(query.toLowerCase().trim()) !== -1 ||
      _user['email']?.toLowerCase().trim().indexOf(query.toLowerCase().trim()) !== -1 ||
      _user['phone']?.toLowerCase().trim().indexOf(query.toLowerCase().trim()) !== -1
    );
  }
  return filteredArray;
}

const ProcurementList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [treeList, setTreeList] = useState([]);
  const gridRef = useRef();

  async function GetList() {
    setLoading(true);
    try {
      const response = await GetAll(apiURL + '/api/v1/procurement-admin-users');
      console.log('ProcurementList API response:', response);
      if (response && response.data && response.data.status) {
        setLoading(false);
        // Ensure data is an array
        const data = response.data.data;
        console.log('ProcurementList data:', data, 'isArray:', Array.isArray(data));
        const dataArray = Array.isArray(data) ? data : [];
        setList(dataArray);
        setTreeList(dataArray); // Set tree list as well
      } else {
        setLoading(false);
        toast.error(response.data.message);
        setList([]);
        setTreeList([]);
      }
    } catch (error) {
      console.error('Error fetching procurement list:', error);
      setLoading(false);
      toast.error('An error occurred while fetching procurement list');
      setList([]);
      setTreeList([]);
    }
  }

  useEffect(() => {
    GetList();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = list.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - list.length) : 0;

  const filteredUsers = applySortFilter(list, getComparator(order, orderBy), filterName);

  const isUserNotFound = filteredUsers.length === 0;


  const handleEditClick = (id) => {
    console.log('Edit clicked with ID:', id);
    if (!id) {
      toast.error('Invalid procurement ID');
      return;
    }
    navigate(`/dashboard/procurement/edit/${id}`);
  };

  const handleApprovalClick = (id) => {
    navigate(`/dashboard/procurement/approval/${id}`);
  };

  const handleAddClick = () => {
    navigate('/dashboard/procurement/create');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'approved':
        return 'success';
      case 'verified':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const isApprovedStatus = (status) => {
    return status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'verified';
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Tree data functions for Procurement Tree
  const filteredTreeUsers = applySortFilter(treeList, getComparator(order, orderBy), filterName);

  // Define columns for tree view
  const treeColumnDefs = [
    {
      field: "first_name",
      filter: "agTextColumnFilter",
      headerName: "Name",
      minWidth: 200,
      cellStyle: { fontSize: '16px' },
      valueGetter: (params) => {
        if (params.data) {
          return `${params.data.first_name || ''} ${params.data.last_name || ''}`.trim();
        }
        return '';
      },
      filterParams: {
        filterOptions: ["contains", "notContains", "startsWith", "endsWith"],
        suppressAndOrCondition: true
      }
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 200,
      cellStyle: { fontSize: '16px' }
    },
    {
      field: "phone",
      headerName: "Phone Number",
      minWidth: 180,
      cellStyle: { fontSize: '16px' }
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      cellStyle: { fontSize: '16px' },
      cellRenderer: (params) => {
        if (params.data) {
          const status = params.data.status?.toLowerCase() === "active" ? 'Approved' : params.data.status || 'Pending';
          return (
            <Chip
              label={status}
              color={getStatusColor(status)}
              variant="filled"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          );
        }
        return '';
      }
    }
  ];

  // Column properties
  const autoGroupColumnDef = {
    headerName: "Procurement Code",
    minWidth: 200
  };

  // Default column definition
  const defaultColDef = {
    flex: 1,
    sortable: true,
    filter: true
  };


  const getDataPath = ({ id, ref_agent_id, emp_id }) => {
    const result = [emp_id];
    let row = filteredUsers.find((row) => row.id === ref_agent_id);
    while (row) {
      result.unshift(row.emp_id);
      row = filteredUsers.find((r) => r.id === row.ref_agent_id);
    }
    return result;
  };
  

  // Handle search input for tree
  const handleTreeFilter = useCallback((event) => {
    setFilterName(event.target.value);
    if (gridRef.current?.api) {
      gridRef.current.api.setQuickFilter(event.target.value);
    }
  }, []);

  return (
    <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
      {/* Professional Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{
              bgcolor: '#1976d2',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}>
              <BusinessIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary">
                Procurement Management
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                View, edit, and approve procurement registrations
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      {/* Tabs */}
      <Zoom in timeout={1000}>
        <Card elevation={4} sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="procurement tabs">
              <Tab label="Procurement Requests" id="procurement-tab-0" aria-controls="procurement-tabpanel-0" />
              <Tab label="Procurement Tree" id="procurement-tab-1" aria-controls="procurement-tabpanel-1" />
            </Tabs>
          </Box>

          {/* Tab Panel 0: Procurement Requests */}
          <TabPanel value={activeTab} index={0}>
            {/* Action Bar */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                  <TextField
                    placeholder="Search by Name, Email, or Phone"
                    value={filterName}
                    onChange={handleFilterByName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 400 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ minWidth: 180 }}
                  >
                    Add Procurement
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={4} sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              width: '100%'
            }}>
              <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                <Table stickyHeader sx={{ width: '100%' }}>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={list?.length}
                numSelected={selected?.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={handleSelectAllClick}
                isCheckbox={false}
              />
              <TableBody>
                {filteredUsers?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                  const { id, first_name, last_name, email, phone, status, created_at } = row;

                  return (
                    <TableRow
                      hover
                      key={id}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" align="left" sx={{ fontWeight: 'bold' }}>
                        <Chip
                          label={index + 1}
                          size="small"
                          sx={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ color: '#1976d2', fontWeight: '600' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                          <Typography variant="body2" fontWeight="600">
                            {capitalCase(first_name + ' ' + last_name)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <EmailIcon sx={{ fontSize: 16, color: '#6c757d' }} />
                          <Typography variant="body2" color="text.secondary">
                            {email}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PhoneIcon sx={{ fontSize: 16, color: '#6c757d' }} />
                          <Typography variant="body2" color="text.secondary">
                            {phone}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">
                        <Chip
                          label={status || 'Pending'}
                          color={getStatusColor(status)}
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {console.log(row.sub_end_date)}
                          <Chip
                            label={ new Date(row.sub_end_date) >=  new Date()  ? 'Active' : 'Inactive'}
                            color={new Date(row.sub_end_date) >=  new Date() ? 'success' : 'default'}
                            variant="filled"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {/* <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewClick(row)}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip> */}
                          <Tooltip title="Edit">
                            <IconButton
                              color="secondary"
                              onClick={() => handleEditClick(id)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {isApprovedStatus(status) ? (
                            <Tooltip title="Already Approved">
                              <IconButton
                                size="small"
                                disabled
                                sx={{
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  cursor: 'not-allowed',
                                  color: '#4caf50',
                                  '& .MuiSvgIcon-root': {
                                    color: '#4caf50'
                                  }
                                }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Review & Approve/Reject">
                              <IconButton
                                color="warning"
                                onClick={() => handleApprovalClick(id)}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 152, 0, 0.2)'
                                  }
                                }}
                              >
                                <GavelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>

              {isUserNotFound && (
                <TableBody>
                  {loading ? <MiscSkeleton record={{ number: 7 }} /> :
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        {list.length <= 0 ?
                          <Typography gutterBottom align="center" variant="subtitle1">No procurement registrations found</Typography> :
                          <SearchNotFound searchQuery={filterName} />
                        }
                      </TableCell>
                    </TableRow>
                  }
                </TableBody>
              )}
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid #e9ecef',
                  backgroundColor: '#f8f9fa',
                  width: '100%'
                }}
              />
            </Card>
          </TabPanel>

          {/* Tab Panel 1: Procurement Tree */}
          <TabPanel value={activeTab} index={1}>
            <Card elevation={4} sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100vh - 400px)',
              minHeight: '600px'
            }}>
              {/* Enhanced Toolbar Section */}
              <Box sx={{  
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e9ecef',
                flexShrink: 0
              }}>
                <UserListToolbar 
                  placeholder="Search Procurement..." 
                  numSelected={selected.length} 
                  filterName={filterName} 
                  onFilterName={handleTreeFilter} 
                />
              </Box>

              {/* AG Grid Container */}
              <Box sx={{ 
                p: 2, 
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div className="ag-theme-alpine" style={{ 
                  height: "100%", 
                  width: "100%",
                  flex: 1,
                  minHeight: 0
                }}>
                  {/* ag-grid component */}
                  <AgGridReact
                    rowData={filteredTreeUsers}
                    treeData={true}
                    animateRows={true}
                    autoGroupColumnDef={autoGroupColumnDef}
                    columnDefs={treeColumnDefs}
                    defaultColDef={defaultColDef}
                    getDataPath={getDataPath}
                    ref={gridRef}
                    pagination={true}
                    paginationPageSize={10}
                    style={{ fontSize: '16px', height: '100%', width: '100%' }}
                  />
                </div>
              </Box>
            </Card>
          </TabPanel>
        </Card>
      </Zoom>


      {/* Professional Stats Footer */}
      <Fade in timeout={1200}>
        <Box sx={{ mt: 4 }}>
          <Paper elevation={2} sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef'
          }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#1976d2',
                    width: 40,
                    height: 40
                  }}>
                    <BusinessIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Registrations
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#ff9800',
                    width: 40,
                    height: 40
                  }}>
                    <PendingIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => !item.status || item.status === 'pending').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending Approval
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#4caf50',
                    width: 40,
                    height: 40
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.status === 'approved').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Approved
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#f44336',
                    width: 40,
                    height: 40
                  }}>
                    <CancelIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.status === 'rejected').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rejected
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Fade>

      {/* Subscription Stats */}
      <Fade in timeout={1400}>
        <Box sx={{ mt: 2 }}>
          <Paper elevation={2} sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: '#e8f5e8',
            border: '1px solid #4caf50'
          }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#4caf50',
                    width: 40,
                    height: 40
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>₹</Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {list.filter(item => item.payment_status === 'paid').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Subscriptions
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#ff9800',
                    width: 40,
                    height: 40
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>₹</Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      ₹{list.filter(item => item.payment_status === 'paid').length * 999}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Revenue
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: '#9c27b0',
                    width: 40,
                    height: 40
                  }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>₹</Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      ₹999
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subscription Fee
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default ProcurementList;
